import { PropsWithChildren, useEffect, useState } from "react";
import { CoObjectItem, isObjectUpdatable } from "../../interfaces/CoObject";
import { useAppSelector } from "../../../redux/hooks/hooks";
import { selectCpmUser, selectProtectionType } from "../../../redux/feature/settings/configurations-slice";
import { elaborationStatusItemIsBlocking } from "../../../redux/feature/object/remote-elaborations-slice";
import { isNotAuthorizedUser } from "../../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { useFilteredElaborations } from "../../generic/GenericElaborationSelector";
import { ObjectsListProps } from "./ObjectsListUpdatePanel";
import { ObjectListItem } from "./ObjectList";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { lockObject } from "../../../redux/feature/cpm/cpm-slice";
import LoadingManager from "../../../common/loading/LoadingManager";

export interface ObjectsListState {
  listObjects: ObjectListItem[];
  showAvailableUpdates: boolean;
  manageMode: boolean;
  wasManageMode: boolean;
  wasShowAvailableUpdates: boolean;
  trackChangesObjectItem: CoObjectItem | undefined;
  confirmDelete: boolean;
}

export const useObjectsListUpdatePanel = (props: PropsWithChildren<ObjectsListProps>) => {
  // component local state
  const [state, setState] = useState<ObjectsListState>({
    listObjects: [],
    showAvailableUpdates: false,
    manageMode: false,
    wasManageMode: false,
    wasShowAvailableUpdates: false,
    trackChangesObjectItem: undefined,
    confirmDelete: false,
  });

  const { documentCoObjects, documentPart, documentPartMetadata, reloadCoObjects } = props;

  useEffect(() => {
    const visibleObjects = state.showAvailableUpdates ? documentCoObjects.filter(isObjectUpdatable) : documentCoObjects;

    const newListObjects: ObjectListItem[] = visibleObjects.map((obj) => {
      const currentObj = state.listObjects.find((o) => o.object.id === obj.id);
      const shouldBeSelectable = !state.showAvailableUpdates || !currentObj?.object.isLocked;
      const shouldBeSelected = shouldBeSelectable && (currentObj?.selected || false);

      return {
        object: obj,
        selected: shouldBeSelected,
        disableSelection: !shouldBeSelectable,
        highlighted: currentObj?.highlighted || false,
        buttons:
          props.page === "objectsList" && !state.showAvailableUpdates && !state.manageMode
            ? [
                {
                  key: `track-changes-btn-${obj.id}`,
                  icon: "/assets/trackchanges.svg",
                  onClick: setTrackChangesObjectItem,
                },
              ]
            : [],
      };
    });

    setState((prev) => ({
      ...prev,
      listObjects: newListObjects,
    }));
  }, [documentCoObjects, props.page, state.showAvailableUpdates, state.manageMode]);

  // application global state selectors
  const user = useAppSelector(selectCpmUser);

  const username: string = user.userName;

  const blockingElaboration = useFilteredElaborations({
    findFunction: elaborationStatusItemIsBlocking,
  });

  let notAuthorizedUser = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OBJECTLIST_PANEL_AVAILABLE_UPDATES,
    user.userFunctions
  );
  let notAuthorizedUserForDelete = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OBJECTLIST_PANEL_DELETE_OBJECTS,
    user.userFunctions
  );
  let notAuthorizedUserForLock = isNotAuthorizedUser(UserFunctionEnum.MENU_LOCK_OBJECT, user.userFunctions);

  // rendering business logic discriminators
  const showBackButton = props.page === "objectsList" && (state.showAvailableUpdates || state.manageMode);
  const showUpdatesButton = props.page === "objectsList" && !state.manageMode && !state.showAvailableUpdates;
  const showManageButton = props.page === "objectsList" && !state.manageMode && !state.showAvailableUpdates;

  const multiselectable =
    props.page === "updates" || (props.page === "objectsList" && state.manageMode) || state.showAvailableUpdates;
  const allowFiltering = props.page === "objectsList";

  const showFooterUpdateButton =
    props.page === "updates" || state.showAvailableUpdates || blockingElaboration != undefined;
  const showFooterDeleteButton = props.page === "objectsList" && state.manageMode && !notAuthorizedUserForDelete;
  let showFooterLockButton: "lock" | "unlock" | "disabled-lock" | boolean;
  if (props.page !== "objectsList" || !state.manageMode || notAuthorizedUserForLock) {
    showFooterLockButton = false;
  } else if (state.listObjects.filter((o) => o.selected && o.object.isLocked).length > 0) {
    showFooterLockButton = "unlock";
  } else if (state.listObjects.filter((o) => o.selected).length > 0) {
    showFooterLockButton = "lock";
  } else {
    showFooterLockButton = "disabled-lock";
  }
  const showFooter =
    !state.confirmDelete && (showFooterUpdateButton || showFooterDeleteButton || showFooterLockButton !== false);

  // --

  async function toggleLockOnSelectedObjects() {
    LoadingManager.create("lock");
    const selectedObjects = state.listObjects.filter((o) => o.selected).map((o) => o.object);
    const lock = state.listObjects.filter((o) => o.selected && o.object.isLocked).length === 0;
    let promises: Promise<void>[] = [];
    for (const obj of selectedObjects) {
      promises.push(lockObject(obj, lock));
    }
    await Promise.all(promises);
    setSelectedObjects([]);
    reloadCoObjects();
    LoadingManager.remove("lock");
  }

  const protectionType: number = useAppSelector(selectProtectionType);

  const getHeaderTitle = () => {
    switch (props.page) {
      case "updates":
        return formatMessage("cof.documentpartupdate.info");
      case "objectsList":
      default:
        if (state.confirmDelete) {
          return formatMessage("cof.objectslist.confirmdeleteshapes.title");
        } else if (state.manageMode) {
          return formatMessage("cof.objectslist.manageobjects");
        } else {
          return formatMessage(
            state.showAvailableUpdates ? "cof.objectslist.availableupdate" : "cof.objectslist.objectslist"
          );
        }
    }
  };

  const setShowAvailableUpdates = (newValue: boolean) => {
    if (newValue && newValue !== state.showAvailableUpdates && state.wasManageMode) {
      setSelectedObjects([]);
    }
    setState((prevState) => ({
      ...prevState,
      showAvailableUpdates: newValue,
      wasManageMode: newValue ? false : prevState.wasManageMode,
      wasShowAvailableUpdates: newValue ? true : prevState.wasShowAvailableUpdates,
    }));
  };

  const setManageMode = (manageMode: boolean = true) => {
    if (manageMode && manageMode !== state.manageMode && state.wasShowAvailableUpdates) {
      setSelectedObjects([]);
    }
    setState((prevState) => ({
      ...prevState,
      manageMode,
      wasShowAvailableUpdates: manageMode ? false : prevState.wasShowAvailableUpdates,
      wasManageMode: manageMode ? true : prevState.wasManageMode,
    }));
  };

  const setTrackChangesObjectItem = (item: CoObjectItem) => {
    setState((prevState) => ({ ...prevState, trackChangesObjectItem: item }));
  };

  const updateSelection = (list: ObjectListItem[], selectedIds: Set<string>) =>
    list.map((obj) => {
      const isSelected = selectedIds.has(obj.object.id);
      return obj.selected === isSelected ? obj : { ...obj, selected: isSelected };
    });

  const setSelectedObjects = (objects: CoObjectItem[]) => {
    const selectedIds = new Set(objects.map((o) => o.id));
    setState((prevState) => ({
      ...prevState,
      listObjects: updateSelection(prevState.listObjects, selectedIds),
    }));
  };

  const setHighlightedObject = (object: CoObjectItem) => {
    setState((prevState) => ({
      ...prevState,
      listObjects: prevState.listObjects.map((obj) => ({
        ...obj,
        highlighted: obj.object.id === object.id,
      })),
    }));
  };

  const setConfirmDelete = (value: boolean = true) => {
    setState((prevState) => ({ ...prevState, confirmDelete: value }));
  };

  return {
    state,
    getHeaderTitle,
    documentPart,
    documentPartMetadata,
    showBackButton,
    notAuthorizedUser,
    showUpdatesButton,
    showManageButton,
    multiselectable,
    protectionType,
    showFooter,
    showFooterUpdateButton,
    showFooterDeleteButton,
    showFooterLockButton,
    allowFiltering,
    username,
    user,
    blockingElaboration,
    setShowAvailableUpdates,
    setManageMode,
    setTrackChangesObjectItem,
    setSelectedObjects,
    setHighlightedObject,
    setConfirmDelete,
    toggleLockOnSelectedObjects,
    reloadCoObjects,
  };
};
